import { isoFractionDigits, roundHalfEven } from '@sentola/money';
import type { FxRateSource, SupportedCurrency } from '@sentola/money';
import type { DailyRateDoc } from '../services/dailyRatesCache';

export type ConvertedBalance = {
    /** In the target currency; null when no rate can express it there. */
    amount: number | null;
    converted: boolean;
    rateDay?: string;
    rateSource?: FxRateSource;
    unavailable?: 'no-rates' | 'no-rate';
};

function rateFor(currency: string, latest: DailyRateDoc): number | undefined {
    return currency === latest.base ? 1 : latest.rates[currency as SupportedCurrency];
}

function sourceFor(currency: string, latest: DailyRateDoc): FxRateSource | undefined {
    return currency === latest.base ? 'same_currency' : latest.sources[currency as SupportedCurrency];
}

/** Unrounded, so callers scaling a whole set of figures do not compound a rounding step each time. */
export function crossRate(from: string, to: string, latest: DailyRateDoc | null): number | null {
    if (from === to) return 1;
    if (!latest) return null;

    const fromRate = rateFor(from, latest);
    const toRate = rateFor(to, latest);
    if (fromRate === undefined || toRate === undefined || fromRate === 0) return null;

    return toRate / fromRate;
}

/**
 * A balance is what it is worth today, so it is marked to market at the latest Daily Rate we hold,
 * not at the rate of any transaction that built it up.
 */
export function convertBalance(
    amount: number,
    fromCurrency: string | undefined,
    targetCurrency: string,
    latest: DailyRateDoc | null
): ConvertedBalance {
    const from = fromCurrency || targetCurrency;
    if (from === targetCurrency) {
        return { amount, converted: false };
    }

    const rate = crossRate(from, targetCurrency, latest);
    if (rate === null) {
        return { amount: null, converted: false, unavailable: latest ? 'no-rate' : 'no-rates' };
    }

    return {
        amount: roundHalfEven(amount * rate, isoFractionDigits(targetCurrency as SupportedCurrency)),
        converted: true,
        rateDay: latest!.date,
        rateSource: sourceFor(from, latest!)
    };
}

export type BalanceItem = { amount: number; currency?: string };

export type BalanceTotal = {
    total: number;
    /** Items left out because no rate could express them in the target currency. */
    excluded: BalanceItem[];
};

/** Total a set of balances in one currency, naming what it could not include. */
export function totalBalances(items: readonly BalanceItem[], targetCurrency: string, latest: DailyRateDoc | null): BalanceTotal {
    let total = 0;
    const excluded: BalanceItem[] = [];

    for (const item of items) {
        const converted = convertBalance(item.amount, item.currency, targetCurrency, latest);
        if (converted.amount === null) {
            excluded.push(item);
            continue;
        }
        total += converted.amount;
    }

    return { total: roundHalfEven(total, isoFractionDigits(targetCurrency as SupportedCurrency)), excluded };
}
